import { Fragment } from "react";
import { StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { Chip } from "./Chip";
import { IconButton } from "./IconButton";

// ChoiceTray — "pick one of these, or make a new one", opened IN PLACE.
//
// Extracted from EQ-1's machine tray (2026-08-13) at the moment the gym chip
// became its second caller. Not a speculative primitive: the shape — a small
// question, a wrapped row of option chips, one trailing chip that mints a new
// option — was already shared by the swap picker's list rows and its
// current-exercise line, and the gym chooser is the same gesture again.
//
// ⛔ Why it had to move out of apps/mobile: the machine tray took the host
// screen's StyleSheet as a prop (ExerciseSwapPicker.styles), so it could only
// ever live inside that one screen. The gym chip mounts in the logger and, via
// packages/ui's ExercisePicker, on web-rnw — a shared component cannot reach
// into a mobile screen's styles. Token-styled and self-contained, it goes
// wherever the hosts do.
//
// ⛔ Deliberately NOT AnchoredMenu (a floating popover that paints over its
// siblings) and NOT Sheet (a bottom-sheet modal). The tray pushes the content
// below it down and stays part of the page: never a modal over a modal, which
// is the property the machine tray was built for in the first place.

export type ChoiceTrayAction = {
  /** Filled when this row is the one in force. Never colour-only: the
   *  IconButton pairs `active` with a solid-gold glyph (a SHAPE change) and
   *  accessibilityState.selected. */
  active: boolean;
  /** ⚠️ Must describe the STATE, not the control: a toggle that announces the
   *  same words pressed and unpressed tells a screen-reader user nothing. */
  accessibilityLabel: string;
  accessibilityHint?: string;
  onPress: () => void;
};

export type ChoiceTrayOption = {
  /** Stable value handed back to `onChoose`. */
  key: string;
  label: string;
  /** Marks where things already are — a statement of fact, not of choice. */
  selected?: boolean;
  /**
   * OPTIONAL secondary action beside the chip (EQ-1-PIN-DEFAULT's ★).
   *
   * ⛔ Opt-in, and the tray's other consumer (the gym chip) passes nothing, so
   * its rows render exactly as before — this widens the contract without
   * touching the caller that did not ask for it.
   *
   * ⚠️ It is a REAL sized box, never a hitSlop halo around a glyph. DS-51
   * proved the difference empirically: a padded invisible area next to another
   * target steals its taps, while a box RESERVES space in the flex row and
   * cannot overlap. Here a stolen tap would attribute the set to the wrong
   * machine, which is the exact damage the tray exists to prevent.
   */
  action?: ChoiceTrayAction;
};

export type ChoiceTrayProps = {
  theme: UiTheme;
  /** The question, e.g. "Which machine?", already localised by the host.
   *  Absent only where the frame says it (a Dialog's title). */
  label?: string;
  /**
   * A leading option standing for the ABSENCE of a value — the unnamed place,
   * the machine nobody named. It comes first because it is where everyone
   * starts and where the history of anyone who never named one still lives.
   *
   * ⛔ It has its OWN callback rather than a key routed through `onChoose`,
   * and that is the whole point of it living here. Both trays used to encode
   * the absence as the empty string and unpick it at the callsite; the second
   * copy of that convention landed with EQ-1-DEFAULT (2026-08-14) and the
   * cheapest fix would have been a third. Modelled here, the sentinel does not
   * exist at all — there is no key to leak, mistype, or store, because absence
   * is a different callback and not a special value.
   */
  defaultOption?: {
    label: string;
    /** True when the host knows nothing is set. Undefined hosts pass false. */
    selected: boolean;
    onChoose: () => void;
    /** ⛔ The SAME action a named row can carry, and it must stay that way: the
     *  machine tray's whole point is that electing the untagged Default is an
     *  answer like any other. It was missing here once and the ★ that mattered
     *  most silently did not exist. */
    action?: ChoiceTrayAction;
  };
  options: readonly ChoiceTrayOption[];
  onChoose: (key: string) => void;
  /**
   * The trailing "make a new one" chip. Optional: a tray that can only pick
   * from what exists omits it rather than rendering a dead control.
   */
  create?: { label: string; onPress: () => void };
  /** Extra line under the chips — a consequence worth stating before the tap. */
  footnote?: string;
  /** Inside a Dialog, whose card is the frame: no band and no edge of its own. */
  bare?: boolean;
};

// ⛔ ONE renderer for every row, default row included. It was two branches for
// half an hour and that was long enough to ship a bug: `defaultOption` had no
// `action` in its type and its branch drew a bare chip, so the ★ the machine
// tray built for the DEFAULT row — the one case the feature exists for — was
// silently discarded. The conditional spread that produced it is not caught by
// excess-property checking, so neither `tsc` nor a test saw it. Two paths that
// must look identical are a place where only one of them gets fixed.
// A pair wider than the band (320 px, font 1.5) pushed the ★ out past the
// padding. The chip gives way (its label wraps to two lines, then ellipsizes)
// and the ★ keeps its box.
const pairChipShrink = { flexShrink: 1, minWidth: 0 } as const;
const pairActionFixed = { flexShrink: 0 } as const;

function renderRow({
  theme,
  styles,
  key,
  label,
  selected,
  onPress,
  action,
}: {
  theme: UiTheme;
  styles: ReturnType<typeof makeStyles>;
  key: string;
  label: string;
  selected: boolean;
  onPress: () => void;
  action?: ChoiceTrayAction;
}) {
  const chip = (
    <Chip
      theme={theme}
      hitSlop={{ top: 8, bottom: 8 }}
      selected={selected}
      label={label}
      onPress={onPress}
      style={action ? pairChipShrink : undefined}
      numberOfLines={action ? 2 : undefined}
    />
  );
  // ⚠️ Two element TYPES at one key: React remounts when the type changes in
  // place, so a row whose `action` appeared or vanished mid-render would lose
  // its subtree. Unreachable today (the picker mounts once per open and `pin`
  // is fixed for that mount), and left as-is rather than always-wrapping so the
  // gym chip's rows keep rendering exactly as they did. ⛔ If a third row shape
  // arrives, wrap unconditionally instead of adding a branch.
  if (!action) return <Fragment key={key}>{chip}</Fragment>;
  return (
    // The chip and its action travel as ONE unit so a wrap can never put the ★
    // on the line below the machine it belongs to.
    <View key={key} style={styles.pair}>
      {chip}
      <IconButton
        theme={theme}
        name="star"
        active={action.active}
        // ⚠️ Vertical-only, mirroring the chip's own. IconButton defaults to
        // `hitSlop={8}`, a BARE NUMBER, which RN expands on all four sides —
        // and with a 4px gap that reaches 4px INTO the chip's visible box on
        // one side and into the next chip's on the other. A stolen tap here
        // does not misclick a filter: it attributes the set to the wrong
        // machine, or silently flips which one is the default. Same discipline
        // as the ✕ beside the gear chip, which tunes its slop asymmetrically
        // for exactly this reason.
        hitSlop={{ top: 8, bottom: 8 }}
        style={pairActionFixed}
        // DS-14's pinned-★ treatment, the same one ProgramRunCard's "current
        // block" already uses: outline → SOLID is a shape change, so the gold
        // reinforces the state rather than carrying it. At reduced acuity a
        // tinted outline and an untinted one are the hardest pair to tell
        // apart, and this is the control that decides which machine a set gets
        // attributed to.
        activeGlyph="fill-pr"
        accessibilityLabel={action.accessibilityLabel}
        {...(action.accessibilityHint
          ? { accessibilityHint: action.accessibilityHint }
          : {})}
        onPress={action.onPress}
      />
    </View>
  );
}

export function ChoiceTray({
  theme,
  label,
  defaultOption,
  options,
  onChoose,
  create,
  footnote,
  bare = false,
}: ChoiceTrayProps) {
  const styles = makeStyles(theme);
  return (
    <View style={bare ? styles.bare : styles.tray}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      {/* Buttons, not radios: `role="radio"` would promise a selection a
          screen reader can query for THIS control, and choosing here usually
          performs the action and unmounts the tray. `selected` marks where
          things already are, and Chip never renders it colour-only — it pairs
          the fill with a leading check. */}
      <View style={styles.chips}>
        {defaultOption
          ? renderRow({
              theme,
              styles,
              key: "__default__",
              label: defaultOption.label,
              selected: defaultOption.selected,
              onPress: defaultOption.onChoose,
              ...(defaultOption.action ? { action: defaultOption.action } : {}),
            })
          : null}
        {options.map((opt) =>
          renderRow({
            theme,
            styles,
            key: opt.key,
            label: opt.label,
            selected: opt.selected ?? false,
            onPress: () => onChoose(opt.key),
            ...(opt.action ? { action: opt.action } : {}),
          }),
        )}
        {create ? (
          <Chip
            theme={theme}
            hitSlop={{ top: 8, bottom: 8 }}
            label={create.label}
            onPress={create.onPress}
          />
        ) : null}
      </View>
      {footnote ? <Text style={styles.footnote}>{footnote}</Text> : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    tray: {
      paddingHorizontal: s.s5,
      paddingTop: s.s3,
      paddingBottom: s.s4,
      gap: s.s2,
      backgroundColor: p.surface2,
      // `surface2` on the host's own ground is ~1.1:1 — a fill alone does not
      // say "this is a separate section". The edge does.
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.borderStrong,
    },
    bare: { gap: s.s2 },
    label: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    // ⛔ No horizontal hitSlop on the chip inside a pair and a real box on the
    // ★, so the two targets touch but never overlap — the gap here is layout,
    // not a shared touch area (DS-39's lesson on the wrapped row's rowGap).
    pair: {
      flexDirection: "row",
      alignItems: "center",
      columnGap: s.s2,
      maxWidth: "100%",
      flexShrink: 1,
    },
    chips: {
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: s.s2,
      // Wider than the column gap on purpose. The chips carry a vertical-only
      // hitSlop of 8, so on a wrapped row a 4px gap would let one chip's touch
      // area cover the NEXT chip's visible box, and a mis-tap here does not
      // misclick a filter, it attributes the set to the wrong machine. Two
      // adjacent slops of 8 need 16: at s5 the bands meet and never overlap.
      rowGap: s.s5,
    },
    // `sm`, not the `xs` micro-label size. This carries real information
    // ("never shared with a coach"), and the smallest type in the system is
    // for one-word uppercase labels — readability beats density.
    footnote: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
  });
}
